import type { AgGridReact, GridOptions } from "~/components/grid/AgGrid";
import type { AnswerLayoutRow } from "~/helpers/aiChat/answerLayouts";
import type { AnswerColumnResult, AnswerLayoutResult } from "~/services/aiOrchestrator";

import { Box } from "@mui/material";
import { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import DashboardKpiTile from "~/components/dashboard/DashboardKpiTile";
import FernieCardShell from "~/components/FernieCardShell";
import answerLayoutColumns from "~/components/fernie/columnDefs/answerLayoutColumnDefs";
import LINE_SX from "~/components/fernie/fernieStyles";
import AgGridWithoutRibbon from "~/components/grid/AgGridWithoutRibbon";
import { BodyText, BodyTextSmall, CaptionText } from "~/components/Typographies";
import answerLayouts from "~/helpers/aiChat/answerLayouts";
import AppThemeContext from "~/providers/AppThemeContext";

interface FernieAnswerLayoutCardProps {
	layout: AnswerLayoutResult;
}

interface AnswerGridProps {
	columns: AnswerColumnResult[];
	rows: string[][];
}

const AnswerGrid = ({ columns, rows }: AnswerGridProps) => {
	const { themeType } = useContext(AppThemeContext);
	const [gridHost, setGridHost] = useState<HTMLDivElement | null>(null);
	const gridRef = useRef<AgGridReact<AnswerLayoutRow>>(null!);
	const scrolls = answerLayouts.scrollsInside(rows.length);
	const rowData = useMemo(() => rows.map((cells, index) => ({ cells, id: String(index) })), [rows]);
	const gridOptions: GridOptions<AnswerLayoutRow> = useMemo(
		() => ({
			columnDefs: answerLayoutColumns(columns),
			domLayout: scrolls ? "normal" : "autoHeight",
			sideBar: false
		}),
		[columns, scrolls]
	);

	return (
		<Box
			data-ag-theme-mode={themeType}
			ref={setGridHost}
			sx={{
				"& .ag-center-cols-viewport": { minHeight: "unset !important" },
				"& .ag-status-bar, & .ag-status-panel-aggregations": { flexWrap: "wrap" },
				height: scrolls ? 392 : undefined
			}}
		>
			{gridHost && (
				<AgGridWithoutRibbon<AnswerLayoutRow>
					classSuffix="fit-height"
					embedded
					getRowId={(params) => params.data.id}
					gridDataKey="fernieAnswerLayout"
					gridOptions={gridOptions}
					gridRef={gridRef}
					rowData={rowData}
					rowSelection={{ checkboxes: false, headerCheckbox: false, mode: "multiRow" }}
					suppressStatePersistence
					themeStyleContainer={gridHost.ownerDocument.head}
				/>
			)}
		</Box>
	);
};

const FernieAnswerLayoutCard = ({ layout }: FernieAnswerLayoutCardProps) => {
	const { t } = useTranslation("common");

	return (
		<FernieCardShell accent="primary" branded tinted>
			<Box
				aria-label={layout.title}
				component="section"
				sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: 2, py: 1.75 }}
			>
				<BodyText bold>{layout.title}</BodyText>
				{layout.sections.map((section, index) => (
					<Box key={`${section.heading}${index}`} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Box>
							<BodyTextSmall bold>{section.heading}</BodyTextSmall>
							{section.partial && (
								<CaptionText sx={{ ...LINE_SX, color: "text.secondary" }}>
									{t("fernieChat.answerLayout.partial")}
								</CaptionText>
							)}
						</Box>
						<Box
							sx={{
								"& .MuiTypography-caption": { whiteSpace: "normal" },
								"& .MuiTypography-h4": { fontSize: "1.125rem", overflowWrap: "anywhere" },
								display: "grid",
								gap: 1,
								gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))"
							}}
						>
							{section.tiles.slice(0, 4).map((tile, tileIndex) => (
								<DashboardKpiTile
									hint={tile.scope}
									key={`${tile.label}${tileIndex}`}
									label={tile.label}
									value={tile.value}
								/>
							))}
						</Box>
						{section.columns.length > 0 && <AnswerGrid columns={section.columns} rows={section.rows} />}
					</Box>
				))}
			</Box>
		</FernieCardShell>
	);
};

export default FernieAnswerLayoutCard;
